import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * CustomersApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/customersApi/customersApi.service.js.
 *
 * `merge` posts to a different domain than the rest (`/v2/api/merge/customers`)
 * — preserved verbatim.
 *
 * `update()` whitelists a fixed payload shape; passing extra keys silently
 * drops them. Notably, `notes` follows the legacy rule: omit to preserve
 * existing value, pass empty string to clear.
 */
const CUSTOMERS_BASE_V2 = '/v2/api/customers';
const CUSTOMERS_MERGE_BASE_V2 = '/v2/api/merge/customers';

const CUSTOMERS_LIST_FIELDS = [...PAGINATION_SEARCH_FIELDS, 'startDate', 'endDate'] as const;

const CUSTOMERS_ADVANCED_SEARCH_FIELDS = [
  ...CUSTOMERS_LIST_FIELDS,
  'firstName',
  'lastName',
  'address',
  'zip',
  'email',
  'customerId',
] as const;

const CUSTOMERS_DETAIL_FIELDS = ['includeAssociatedMemberships', 'includeMergeHistory'] as const;

interface CustomerUpdatePayload {
  prefix?: string;
  firstName: string;
  lastName: string;
  solicitationCodes?: unknown[];
  email: string;
  customerId: string;
  phoneNumber?: string;
  orgName?: string;
  externalCustomerImportId?: string;
  address?: unknown[];
  attributes?: unknown[];
  middleName?: string;
  id: number | string;
  customerSource?: string;
  notes?: string;
}

@Injectable({ providedIn: 'root' })
export class CustomersApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(CUSTOMERS_BASE_V2, {
        params: buildParams(props, CUSTOMERS_LIST_FIELDS),
      }),
    );
  }

  advancedSearch(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${CUSTOMERS_BASE_V2}/advancedsearch`, {
        params: buildParams(props, CUSTOMERS_ADVANCED_SEARCH_FIELDS),
      }),
    );
  }

  detail(props: {
    customerId: number | string;
    includeAssociatedMemberships?: boolean;
    includeMergeHistory?: boolean;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${CUSTOMERS_BASE_V2}/${props.customerId}`, {
        params: buildParams(props, CUSTOMERS_DETAIL_FIELDS),
      }),
    );
  }

  update(props: CustomerUpdatePayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(CUSTOMERS_BASE_V2, {
        prefix: props.prefix,
        firstName: props.firstName,
        lastName: props.lastName,
        solicitationCodes: props.solicitationCodes,
        email: props.email,
        customerId: props.customerId,
        phoneNumber: props.phoneNumber,
        orgName: props.orgName,
        externalCustomerImportId: props.externalCustomerImportId,
        address: props.address,
        attributes: props.attributes,
        middleName: props.middleName,
        id: props.id,
        customerSource: props.customerSource,
        notes: props.notes,
      }),
    );
  }

  merge(props: { baseId: string; duplicateIds: string[] }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(CUSTOMERS_MERGE_BASE_V2, {
        baseId: props.baseId,
        duplicateIds: props.duplicateIds,
      }),
    );
  }
}
